import * as React from 'react';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import { DropdownData } from '../../../components_v2/dropdown/model/Model';
import { Table } from '../../../components_v2/table/Table';
import { ACTIONS_PER_OBJECT, ObjectActionScopes } from '../../../Types';
import getPermissions from '../../permissions/getPermissions';
import { getGroupPolicies, getPlanPolicies } from '../action';
import { CerberusAuthPolicy, Group, ObjectActionScope, PlanPolicy } from '../model';
import { NameInput, Title } from '../style';
import { Translate, translateToString } from '../../../styles/global/translate';
import { YesNoCheckbox } from '../../forms/PopupField';
import { ObjectAction, Scope } from '../../../web-types/objectActions';

export const OBJECT_ACTION_ACTIONS = ['view', 'read', 'create', 'update', 'delete'];

export function capitalizeFirstLetter(string: string) {
	return string.charAt(0).toUpperCase() + string.slice(1);
}

type LocalPermission = {
	[key in ObjectAction]: number;
}

export default function PermissionTable(props: {
	groupsName: string[]
	group?: Group
	setGroupName: (name: string) => void
	onDelete: () => void
	onUpdate: (values: ObjectActionScope[]) => void
}): JSX.Element {
	const [groupPolicies, setGroupPolicies] = React.useState<CerberusAuthPolicy[]>([]);
	const [planPolicies, setPlanPolicies] = React.useState<LocalPermission>();
	const [name, setName] = React.useState<string>(props.group?.name ?? '');
	const [policyModifies, setPolycyModifies] = React.useState<ObjectActionScope[]>([]);
	const planPermissions = Object.keys(getPermissions());

	function localGetGroupPolicies(): void {
		getGroupPolicies(props.group?.id ?? -1).then(res => {
			setGroupPolicies(res);
			const policies = res.map((d: CerberusAuthPolicy): ObjectActionScope => ({
				groupId: d.group_id,
				policyId: d.id,
				scope: d.scope,
				objectAction: d.object_action,
				toRemove: false
			}));
			setPolycyModifies(policies);
			props.onUpdate(policies);
		}).catch(console.log);
	}

	React.useEffect(() => {
		localGetGroupPolicies();
	}, [props.group?.id]);

	React.useEffect(() => {
		getPlanPolicies()
			.then(res => {
				setPlanPolicies(res.reduce((acc: LocalPermission, per: PlanPolicy) => {
					acc[per.object_action] = per.id;
					return acc;
				}, {} as LocalPermission));
			})
			.catch(console.error);
	}, []);

	function updatePolicy(groupId: number, policyId: number | undefined, objectAction: ObjectAction | undefined, scope: Scope | undefined): void {
		const newList = policyModifies.filter(n => n.objectAction != objectAction);

		const newPolicy: ObjectActionScope = { groupId, policyId, objectAction, scope, toRemove: scope == undefined };
		newList.push(newPolicy);
		setPolycyModifies(newList);
		props.onUpdate(newList);
	}

	const { columns, data } = React.useMemo(
		() => ({
			columns: [
				{
					id: 'Actions/Objects',
					Header: ' ',
					accessor: (objectActions: ObjectActionScopes[]) => {
						const t = objectActions[0]?.objectAction?.split(/(?=[A-Z])/)?.slice(1).join('_').toLowerCase();
						return <div id={`row-${t}`}>{translateToString(`permission.objects.${t}`)}</div>;
					},
					disableSortBy: true,
					width: 150
				},
				...OBJECT_ACTION_ACTIONS.map((action: string) => {
					return {
						id: action,
						Header: () => <div id={`header-${action}`}>{translateToString(`permission.actions.${action}`)}</div>,
						accessor: (row: ObjectActionScopes[]) => {
							const capitalizedAction = capitalizeFirstLetter(action);
							const objectActionScope = row.find(objectAction => objectAction.objectAction.startsWith(capitalizedAction));
							// if object-action doesnt exist display nothing
							if (objectActionScope == undefined) return <></>;

							const policy = policyModifies.find(gp => gp.objectAction == objectActionScope.objectAction);

							if (objectActionScope.scopes.length == 2 && objectActionScope.scopes.some((s) => s === 'Company')) {
								const checked = policy === undefined || policy.scope === 'Company';
								return <YesNoCheckbox checked={checked} onChange={() => {
									let value: Scope;
									if (checked) {
										value = objectActionScope.scopes.find((s) => s !== 'Company');
									} else {
										value = 'Company';
									}
									updatePolicy(props.group?.id ?? -1, policy?.policyId, objectActionScope.objectAction, value);
								}} />;
							}

							const maped: Array<DropdownData<Scope | undefined>> = objectActionScope.scopes.map(scope => ({ value: scope, label: translateToString(`permission.scope.${scope}`) }));

							return <Dropdown
								autoOptionUp
								readOnly
								name={`dropdown[${objectActionScope.objectAction}]`}
								datalist={maped}
								dropdownStyle={{
									width: '100%',
									optionWidth: '200px',
									optionHeight: '150px',
									backgroundColor: 'transparent'
								}}
								selectedValue={policy ? { value: policy.scope, label: translateToString(`permission.scope.${policy.scope}`) } : { value: 'Company', label: translateToString('permission.scope.Company') }}
								onChange={({ value }: DropdownData<Scope | undefined>) => {
									updatePolicy(props.group?.id ?? -1, policy?.policyId, objectActionScope.objectAction, value);
								}}
							></Dropdown>;
						},
						disableSortBy: true,
						width: undefined
					};
				})
			],
			data: ACTIONS_PER_OBJECT.map((objectActions: ObjectActionScopes[]): ObjectActionScopes[] => objectActions.filter(oa => planPermissions.includes(oa.objectAction))).filter(oa => oa.length > 0)
		}),
		[groupPolicies, planPolicies, policyModifies]
	);

	return (
		<div style={{ marginTop: 20 }}>
			<Title>
				<Translate id="admin.group_name" />
			</Title>
			<NameInput
				error={props.groupsName.some(gName => gName == name)}
				type='text'
				value={name}
				onChange={e => {
					setName(e.target.value);
					props.setGroupName(e.target.value);
				}}
			/>
			<Title>
				<Translate id="admin.permissions" />
			</Title>
			<Table
				height='calc(100vh - 269px)'
				columns={columns}
				data={data}
			/>

		</div>
	);
}
